import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { AppProvider } from '@shopify/polaris';
import { DisplayText } from '@superops/ui-design-system';
import {
  ALL_MODULES,
  AppContext,
  AttachmentPreviewer,
  BrandingLogo,
  DateTimeFormatter,
  Loader
} from '@nile/components';
import {
  dompurifier,
  generateString,
  getDownloadURL
} from '@nile/components/src/Utils/helpers';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import {
  GET_EXPANDED_CLIENT_CONVERSATION_CONTENT,
  GET_EXPANDED_TICKET_CONVERSATION_CONTENT,
  GET_EXPANDED_VENDOR_CONVERSATION_CONTENT,
  TicketClient,
  AccountClient
} from '@nile/graphql';
import VirtualizedHtml from './VirtualizedHtml';
// import VirtualizedHTML1 from './VirtualizedHTML1';

const MinifyEmailTemplate = () => {
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [conversationData, setConversationData] = useState(null);
  const [formatterAttachment, setFormatterAttachment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryVariable, setQueryVariable] = useState(null);

  const appContext = useContext(AppContext);

  const { conversationtype, module, typeid, id } = useParams();

  useEffect(() => {
    switch (conversationtype) {
      case 'ticketconversation':
        setQueryVariable({
          input: {
            ticket: {
              ticketId: typeid
            },
            conversation: {
              conversationId: id
            }
          }
        });
        fetchTicketMinifiedConversation();
        break;
      case 'clientconversation':
        setQueryVariable({
          input: {
            client: {
              accountId: typeid
            },
            conversation: {
              conversationId: id
            }
          }
        });
        fetchClientMinifiedConversation();
        break;
      case 'vendorconversation':
        setQueryVariable({
          input: {
            workItem: {
              workId: typeid,
              module: ALL_MODULES[module]
            },
            conversation: {
              conversationId: id
            }
          }
        });
        fetchVendorMinifiedConversation();
        break;
    }
  }, [id]);

  // const randomId = generateString(5) || 'abcde';

  // useEffect(() => {
  //   if (
  //     document.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
  //   ) {
  //     document
  //       ?.querySelector(`.${randomId}`)
  //       ?.querySelectorAll('img')
  //       ?.forEach((img) => {
  //         img.classList.add('cursor-zoom-in', 'mw100');
  //         img.addEventListener('click', imageDownloader);
  //       });
  //     return () => {
  //       document
  //         ?.querySelector(`.${randomId}`)
  //         ?.querySelectorAll('img')
  //         ?.forEach((img) => {
  //           img.classList.remove('cursor-zoom-in');
  //           img.removeEventListener('click', imageDownloader);
  //         });
  //     };
  //   }
  // }, []);

  const [
    fetchTicketMinifiedConversation,
    { data: ticketMinifiedconversation, loading: ticketloading }
  ] = useLazyQuery(GET_EXPANDED_TICKET_CONVERSATION_CONTENT, {
    client: TicketClient,
    fetchPolicy: 'network-only',
    variables: queryVariable,
    onCompleted({ getExpandedTicketConversationContent }) {
      getExpandedTicketConversationContent &&
        setConversationData(getExpandedTicketConversationContent);
      setLoading(false);
    }
  });

  const [
    fetchClientMinifiedConversation,
    { data: clientMinifiedconversation, loading: clientloading }
  ] = useLazyQuery(GET_EXPANDED_CLIENT_CONVERSATION_CONTENT, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: queryVariable,
    onCompleted({ getExpandedClientConversationContent }) {
      getExpandedClientConversationContent &&
        setConversationData(getExpandedClientConversationContent);
      setLoading(false);
    }
  });

  const [
    fetchVendorMinifiedConversation,
    { data: vendorMinifiedconversation, loading: vendorloading }
  ] = useLazyQuery(GET_EXPANDED_VENDOR_CONVERSATION_CONTENT, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: queryVariable,
    onCompleted({ getExpandedVendorConversationContent }) {
      getExpandedVendorConversationContent &&
        setConversationData(getExpandedVendorConversationContent);
      setLoading(false);
    }
  });

  const renderUsersEmail = (arr = []) => {
    return arr.map((user, index) => {
      if (user?.user?.email) {
        return (
          <DisplayText element="h3" weight="Medium">
            {user?.user?.email} {index !== arr.length - 1 && ','}
          </DisplayText>
        );
      }
    });
  };

  const getAttachmentURL = (item) => {
    let attachmentModule = '';
    let details = {
      fileName: item?.fileName,
      conversationId: conversationData?.conversationId
    };
    switch (conversationtype) {
      case 'ticketconversation':
        attachmentModule = ALL_MODULES.ticketConversationAttachment;
        details.ticketId = typeid;
        break;
      case 'clientconversation':
        attachmentModule = ALL_MODULES.clientConversationAttachment;
        details.clientId = typeid;
        break;
      case 'vendorconversation':
        attachmentModule = ALL_MODULES.sideConversationAttachment;
        details.workItem = {
          workId: typeid,
          module: ALL_MODULES[module]
        };
        break;
    }
    const url = getDownloadURL({ module: attachmentModule, details });
    return url;
  };

  const formatAttachmentForPreview = (attachments, cb) => {
    const formattedItems =
      attachments?.map((item) => {
        if (item) {
          return {
            name: item.fileName,
            url: getAttachmentURL(item),
            type: item.fileName.substr(
              item.fileName.lastIndexOf('.') + 1,
              item.fileName.length
            )
          };
        }
      }) || [];
    setFormatterAttachment(formattedItems);
    cb();
  };

  const openAttachmentPreview = useCallback(
    (index, attachments, item) => {
      if (attachments.length) {
        formatAttachmentForPreview(attachments, () => {
          setShowAttachmentPreview({
            ...showAttachmentPreview,
            show: true,
            index
          });
        });
      }
    },
    [conversationData]
  );

  const closePreviwer = useCallback(() => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  }, [conversationData]);

  const renderAttachments = (attachments = []) => {
    return attachments.map((item, index) => {
      return (
        <AttachmentHolder
          onClick={() => openAttachmentPreview(index, attachments, item)}
          fileName={item.fileName || ''}
          fontWeight="bolder"
        />
      );
    });
  };

  if (loading) {
    return (
      <div className="vh100 fx actr jcenter w100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="full-height full-width bg-white p20 scroll">
      <BrandingLogo enableFavIconBrand />
      {conversationData?.subject && (
        <div className={'border-top-bottom-grey p8'}>
          <DisplayText element="h2" weight="bold">
            {conversationData?.subject || ''}
          </DisplayText>
        </div>
      )}
      <div className="flex fx-sb p8">
        <div className="flex fcol g3">
          <div className="flex g2">
            <DisplayText element="h3" weight="Bold">
              {conversationData?.user?.name || ''}
            </DisplayText>
            <DisplayText element="h3" weight="Medium">
              {`<${conversationData?.user?.email || ''}>`}
            </DisplayText>
          </div>
          {conversationData?.user?.email && (
            <DisplayText element="h3" weight="Medium">
              Reply-To: {conversationData?.user?.email || ''}
            </DisplayText>
          )}
          {conversationData?.toUsers?.length && (
            <div className="flex g2">
              <DisplayText element="h3" weight="Medium">
                To:
              </DisplayText>
              <div>{renderUsersEmail(conversationData.toUsers)}</div>
            </div>
          )}
          {conversationData?.ccUsers?.length && (
            <div className="flex g2">
              <DisplayText element="h3" weight="Medium">
                CC:
              </DisplayText>
              <div>{renderUsersEmail(conversationData.ccUsers)}</div>
            </div>
          )}
          {conversationData?.bccUsers?.length && (
            <div className="flex g2">
              <DisplayText element="h3" weight="Medium">
                BCC:
              </DisplayText>
              <div>{renderUsersEmail(conversationData.bccUsers)}</div>
            </div>
          )}
        </div>
        <div>
          <DisplayText element="h4" weight="Bold">
            <DateTimeFormatter
              type="dateTime"
              date={conversationData?.time}
              company={appContext?.company}
            />
          </DisplayText>
        </div>
      </div>
      <div className="p8">
        <VirtualizedHtml rawHtml={conversationData?.content} />
        {/* Todo - optimised way to load html data
        <VirtualizedHTML1 rawHtml={conversationData?.content} /> */}
      </div>
      {conversationData?.attachments?.length && (
        <div className="flex wrap g10">
          {renderAttachments(conversationData?.attachments)}
        </div>
      )}
      {showAttachmentPreview.show && (
        <AttachmentPreviewer
          showPreviewer={showAttachmentPreview.show}
          closePreviwer={closePreviwer}
          list={formatterAttachment}
          selectedIndex={showAttachmentPreview.index}
        />
      )}
    </div>
  );
};

const ConversationTemplate = () => {
  return (
    <AppProvider>
      <MinifyEmailTemplate />
    </AppProvider>
  );
};

export default ConversationTemplate;
